// English translations. Keys mirror src/i18n/zh.ts.

export const en: Record<string, string> = {
  'app.title': 'Linear Algebra · Visual',
  'app.sub': 'After Kenji Hiranabe',
  'nav.home': 'Home',
  'nav.chapters': 'Chapters',
  'nav.about': 'About',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  lang: 'EN',

  ch: 'Ch',
  prev: 'Prev',
  next: 'Next',
  'fig.geometry': 'Geometric view',
  'panel.numbers': 'Numbers',
  'panel.controls': 'Controls',
  'panel.matrix': 'Matrix',
  'panel.legend': 'Legend',

  'home.eyebrow': 'After Kenji Hiranabe — The Art of Linear Algebra',
  'home.title': 'Make matrices\ngeometry\nyou can grab.',
  'home.lede':
    'Behind every Ax = b is a stretch, a rotation, a collapse. This site turns those drawings into things you can drag. Pull a vector — the parallelogram updates. Edit a cell — the principal axes realign.',
  'home.start': 'Start →',
  'home.allchapters': 'Eleven chapters · all interactive',
  'home.stat.chapters': 'chapters',
  'home.stat.interact': 'draggable',
  'home.stat.decomp': 'decompositions',
  'home.stat.dim': 'dim ≤ 10',

  'ch01.title': 'Four views of a matrix',
  'ch01.sub': 'The same matrix is numbers, columns, rows, or a sum of rank-1 pieces.',
  'ch01.short': '4 views',
  'ch02.title': 'Vector × Vector',
  'ch02.sub': 'Dot, outer, projection — drag to see what each really is.',
  'ch02.short': 'Dot / outer',
  'ch03.title': 'Matrix × Vector',
  'ch03.sub': 'Ax is a linear combination of A’s columns; it walks the column space.',
  'ch03.short': 'Linear combo',
  'ch04.title': 'Four subspaces',
  'ch04.sub': 'C(A), N(A), C(Aᵀ), N(Aᵀ) — domain split, range split.',
  'ch04.short': 'Four spaces',
  'ch05.title': 'Matrix × Matrix',
  'ch05.sub': 'Dot, columns, rows, outer — four readings of the same product.',
  'ch05.short': '4 readings',
  'ch06.title': 'Patterns: P · D · E · R',
  'ch06.sub': 'Permute, diagonal, eliminate, rotate — what they look like.',
  'ch06.short': 'P D E R',
  'ch07.title': 'A = CR',
  'ch07.sub': 'Pick independent columns; rank decomposition.',
  'ch07.short': 'CR',
  'ch08.title': 'A = LU',
  'ch08.sub': 'Gaussian elimination, step by step.',
  'ch08.short': 'LU',
  'ch09.title': 'A = QR',
  'ch09.sub': 'Gram–Schmidt orthogonalizes the columns.',
  'ch09.short': 'QR',
  'ch10.title': 'Symmetric eigendecomposition',
  'ch10.sub': 'Principal axes and stretches — A = QΛQᵀ, geometrically.',
  'ch10.short': 'EVD',
  'ch11.title': 'Singular value decomposition',
  'ch11.sub': 'Rotate → scale → rotate, the three-step soul of every matrix.',
  'ch11.short': 'SVD',
  'ch12.title': 'Five factorizations at a glance',
  'ch12.sub': 'CR · LU · QR · QΛQᵀ · UΣVᵀ — change A, watch all five.',
  'ch12.short': '5 factorizations',
  'ch13.title': 'Map of eigenvalues',
  'ch13.sub': 'Where do λ live in the complex plane? Twelve archetypes, one matrix.',
  'ch13.short': 'λ map',
  'ch14.title': 'Matrix world',
  'ch14.sub': 'A nested map of every matrix family — light up the rings A belongs to.',
  'ch14.short': 'Matrix world',
  'ch15.title': 'Solutions to Ax = b',
  'ch15.sub': 'Particular + null space — the four subspaces with the solution geometry overlaid.',
  'ch15.short': 'Ax = b',
  'ch16.title': 'Practical patterns atlas',
  'ch16.sub': 'P1 · P2 · P1′ · P2′ · P3 · P4 — six algebraic shapes of multiplication.',
  'ch16.short': 'Patterns',

  'ch01.numbers': 'Numbers',
  'ch01.columns': 'Columns',
  'ch01.rows': 'Rows',
  'ch01.rank1': 'Rank-1 sum',
  'ch01.numbers.cap': 'A grid of numbers — no geometry yet.',
  'ch01.columns.cap': 'Columns a₁, a₂ — Ax is a linear combo of them.',
  'ch01.rows.cap': 'Rows r₁, r₂ — each takes a dot product with the input.',
  'ch01.rank1.cap': 'A = Σ σᵢ uᵢ vᵢᵀ. Each piece is one outer product.',
  'ch01.matrix': 'Matrix A',
  'ch01.expansion': 'Expansion',
  'ch01.numbers.body':
    'The plainest reading: just numbers. But that tells you nothing geometric. Switch the tabs to see what those numbers actually mean.',
  'ch01.meta': 'One matrix · four geometries',

  'ch02.drag': 'Drag the tip of a or b.',
  'ch02.outer': 'Outer product abᵀ',
  'ch02.outer.note': 'The outer product is rank-1 — every column is a multiple of a.',

  'ch03.drag': 'Drag x — see Ax move in real time.',
  'ch03.matrix': 'A · x = Ax',
  'ch03.linear': 'Linear combination',
  'ch03.note':
    'Key: Ax always lands in the column space of A. Moving x just changes the combination weights.',
  'ch03.colspace': 'Column space C(A)',
  'ch03.row.title': 'Row reading: xᵀA',
  'ch03.row.note':
    'The dual reading: xᵀA is a linear combination of the *rows* of A. Same matrix, two stories.',

  'ch04.input': 'Input space',
  'ch04.output': 'Output space',
  'ch04.input.cap': 'C(Aᵀ) row space and N(A) null space — orthogonal complements.',
  'ch04.output.cap': 'C(A) column space and N(Aᵀ) left null — orthogonal complements.',
  'ch04.summary': 'Four subspaces',
  'ch04.colspace': 'Column space — where outputs live',
  'ch04.rowspace': 'Row space — input directions A actually sees',
  'ch04.nullspace': 'Null space — inputs sent to zero',
  'ch04.leftnull': 'Left null — output directions A misses',
  'ch04.note':
    'When A is full rank, both null spaces are just the origin. Make row 2 a multiple of row 1 — you’ll see rank collapse geometrically.',

  'ch05.dot': 'Dot view',
  'ch05.columns': 'Column view',
  'ch05.rows': 'Row view',
  'ch05.outer': 'Outer sum',
  'ch05.fourways': 'four readings',
  'ch05.input': 'Input A · B',
  'ch05.expansion': 'Expansion',
  'ch05.dot.body': 'Classic: (AB)ᵢⱼ is row i of A dotted with column j of B.',
  'ch05.columns.body':
    'Column j of AB = A times column j of B. Each output column is a combo of A’s columns.',
  'ch05.rows.body':
    'Row i of AB = row i of A times B. Each output row is a combo of B’s rows.',
  'ch05.outer.body':
    'AB = Σ aₖ bₖᵀ — split into rank-1 pieces. This is the seed of SVD.',

  'ch06.perm': 'Permute P',
  'ch06.diag': 'Diagonal D',
  'ch06.elim': 'Eliminate E',
  'ch06.rot': 'Rotate R',
  'ch06.cap': 'Watch the unit grid deform.',
  'ch06.what': 'What it does',
  'ch06.permutation.note':
    'Swaps two rows (or columns). Geometrically a mirror. det = ±1, always orthogonal.',
  'ch06.diagonal.note':
    'Stretches along the axes — ×2 in x, ÷2 in y. The simplest non-trivial transform.',
  'ch06.elimination.note':
    'Subtracts a multiple of row 1 from row 2. A shear; column space unchanged.',
  'ch06.rotation.note':
    'Rotates by an angle. det = 1, columns orthonormal. The true identity of U and V in SVD.',

  'ch07.colpick': 'Pick independent columns',
  'ch07.cap': 'If columns are parallel → rank 1, keep one.',
  'ch07.note':
    'CR splits A into independent columns (C) × combination weights (R). When columns are dependent, R says how to rebuild them.',
  'ch07.patterns.title': 'P1 / P2 — same A, two readings',
  'ch07.p1.label': 'columns of A as linear combos of C',
  'ch07.p2.label': 'rows of A as linear combos of R',
  'ch07.patterns.rank0':
    'Rank 0 — A is the zero matrix; both C and R collapse, no independent columns or rows.',
  'ch07.patterns.rank1':
    'Rank 1 — pick one independent column (P1), one independent row (P2). R encodes the dependence.',
  'ch07.patterns.rank2':
    'Rank 2 — every column independent (P1 keeps both); every row independent (P2 keeps both); R = I.',

  'ch08.gaussian': 'Gaussian elimination',
  'ch08.cap': 'Watch the row vectors rotate as we eliminate.',
  'ch08.singular': 'Pivot is 0; LU needs a permutation.',
  'ch08.step.input': 'Initial',
  'ch08.step.eliminate': 'Eliminate row 2',
  'ch08.step.factored': 'Done',
  'ch08.s0': 'Start with two row vectors. We want to remove the x-component from r₂.',
  'ch08.s1': 'Subtract ℓ × r₁ from r₂ to kill its x-component. That ℓ is the entry of L.',
  'ch08.s2': 'A = L · U. L records the multipliers, U is upper-triangular.',
  'ch08.peeling.title': 'Rank-1 peeling',
  'ch08.peeling.note':
    'Another reading of LU: A = (column 1 of L)·(row 1 of U)ᵀ + residual. For 2×2 the residual is zero — one outer product peeled, A is gone.',

  'ch09.cap': 'Step by step: orthogonalize the columns, recording R as we go.',
  'ch09.step.input': 'Two columns',
  'ch09.step.q1': 'Normalize a₁',
  'ch09.step.subtract': 'Subtract projection',
  'ch09.step.q2': 'Orthonormal basis',
  'ch09.s0': 'Start with A’s two columns. Generally not orthogonal, not unit length.',
  'ch09.s1': 'Normalize a₁ → q₁. The diagonal entry r₁₁ = ||a₁||.',
  'ch09.s2': 'Subtract a₂’s projection onto q₁. What remains is perpendicular to q₁.',
  'ch09.s3': 'Normalize the remainder → q₂. Q = [q₁ | q₂] is orthogonal.',

  'ch10.cap':
    'Drag x. When red Ax aligns with a principal axis, A is just stretching — that’s an eigenvector.',
  'ch10.symA': 'Symmetric A',
  'ch10.eigen': 'Eigenvalues & vectors',
  'ch10.symmetric': 'Symmetric ⇒ real eigenvalues + orthogonal eigenvectors',
  'ch10.note':
    'A symmetric matrix has orthogonal eigenvectors. So A stretches by λ₁ along q₁ and by λ₂ along q₂ — no rotation.',
  'ch10.complex':
    'Eigenvalues are complex — there is rotation. This page focuses on real symmetric A.',

  'ch11.three': 'three-step geometry',
  'ch11.cap': 'Blue ellipse = unit circle, after the current step.',
  'ch11.steps': 'Steps',
  'ch11.factors': 'Factors',
  'ch11.step.input': 'Unit circle',
  'ch11.step.rotate1': 'Vᵀ · rotate',
  'ch11.step.scale': 'Σ · scale',
  'ch11.step.rotate2': 'U · rotate',
  'ch11.s0':
    'Start with the unit circle. Any matrix can be written as three things done to it: rotate → scale → rotate.',
  'ch11.s1':
    'Vᵀ is a pure rotation — it spins the input into the principal singular directions.',
  'ch11.s2':
    'Σ stretches along the axes: σ₁ in x, σ₂ in y. The circle becomes an axis-aligned ellipse.',
  'ch11.s3':
    'U rotates again, sending the ellipse to A’s output orientation. Done: A = U Σ Vᵀ.',

  'ch12.eyebrow': 'Five factorizations · summary',
  'ch12.input': 'Input matrix A',
  'ch12.note':
    'Every factorization tells a different story about A. CR finds independent columns; LU is elimination; QR is Gram–Schmidt; QΛQᵀ is the symmetric eigendecomposition; UΣVᵀ works for every matrix. After Hiranabe & Strang, CC-BY.',
  'ch12.cr.title': 'A = CR',
  'ch12.cr.note': 'C: independent columns of A. R: row echelon form. Rank = number of columns in C.',
  'ch12.lu.title': 'A = LU',
  'ch12.lu.note': 'L: unit lower triangular (multipliers). U: upper triangular (after elimination).',
  'ch12.lu.fail': 'Pivot is zero — LU needs row permutation (PA = LU).',
  'ch12.qr.title': 'A = QR',
  'ch12.qr.note': 'Q: orthonormal columns (Gram–Schmidt). R: upper triangular (the projections).',
  'ch12.evd.title': 'A = QΛQᵀ',
  'ch12.evd.note': 'Q: orthonormal eigenvectors. Λ: eigenvalues on the diagonal. Symmetric matrices only.',
  'ch12.evd.fail': 'A is not symmetric — QΛQᵀ does not apply. Try [[2, 1], [1, 2]].',
  'ch12.svd.title': 'A = UΣVᵀ',
  'ch12.svd.note': 'U, V: orthonormal bases. Σ: singular values on the diagonal. Works for every matrix.',
  'ch12.rank': 'rank',
  'ch12.symmetric.yes': 'symmetric',
  'ch12.symmetric.no': 'not symmetric',

  'ch13.eyebrow': 'Where eigenvalues live',
  'ch13.kinds': 'A is in:',
  'ch13.kinds.empty': 'No special class.',
  'ch13.reference': 'Reference: 12 archetypes',
  'ch13.note':
    'After Hiranabe, Map of Eigenvalues (CC-BY). Each cell shows a matrix archetype, the region where its eigenvalues must live, and a representative example.',
  'ch13.k.zero': 'Zero · O',
  'ch13.k.identity': 'Identity · I',
  'ch13.k.projection': 'Projection · P=P²=Pᵀ',
  'ch13.k.nilpotent': 'Nilpotent · Nᵏ=O',
  'ch13.k.scalar': 'Scalar · zI',
  'ch13.k.symmetric': 'Symmetric · S=Sᵀ',
  'ch13.k.posdef': 'Positive definite · S₊',
  'ch13.k.antisym': 'Anti-symmetric · A=−Aᵀ',
  'ch13.k.orthogonal': 'Orthogonal · QQᵀ=I',
  'ch13.k.markov': 'Markov · 1ᵀA=1',
  'ch13.k.invertible': 'Invertible · |A|≠0',
  'ch13.k.singular': 'Singular · |A|=0',
  'ch13.k.diagonal': 'Diagonal',
  'ch13.k.possemi': 'Positive semi-definite',

  'ch14.eyebrow': 'A nested atlas',
  'ch14.invertible': 'Invertible',
  'ch14.singular': 'Singular',
  'ch14.samples': 'Try it on:',
  'ch14.note':
    'After Hiranabe, Matrix World (CC-BY). Each ring is a family; A lights up every family it belongs to. Inner rings are stricter (more structure).',
  'ch14.s.identity': 'Identity I',
  'ch14.s.zero': 'Zero O',
  'ch14.s.symmetric': 'Symmetric',
  'ch14.s.orthogonal': 'Orthogonal (rot 60°)',
  'ch14.s.permutation': 'Permutation [[0,1],[1,0]]',
  'ch14.s.projection': 'Projection [[1,0],[0,0]]',
  'ch14.s.markov': 'Markov',
  'ch14.s.defective': 'Defective [[1,1],[0,1]]',
  'ch14.s.singular': 'Singular [[1,2],[2,4]]',
  'ch14.k.matrix': 'Matrix (m×n)',
  'ch14.k.diagonalizable': 'Diagonalizable',
  'ch14.k.normal': 'Normal · AᵀA = AAᵀ',
  'ch14.k.symmetric': 'Symmetric',
  'ch14.k.possemi': 'Positive semi-def.',
  'ch14.k.posdef': 'Positive def.',
  'ch14.k.orthogonal': 'Orthogonal',
  'ch14.k.permutation': 'Permutation',
  'ch14.k.projection': 'Projection',
  'ch14.k.diagonal': 'Diagonal',
  'ch14.k.identity': 'Identity',
  'ch14.k.zero': 'Zero',
  'ch14.k.scalar': 'Scalar',
  'ch14.k.markov': 'Markov',
  'ch14.k.invertible': 'Invertible',
  'ch14.k.singular': 'Singular',
  'ch14.k.antisym': 'Anti-symmetric',
  'ch14.k.nilpotent': 'Nilpotent',

  'ch15.eyebrow': 'Four subspaces · solution geometry',
  'ch15.input.A': 'A',
  'ch15.input.b': 'b',
  'ch15.input.space': 'Input space ℝⁿ',
  'ch15.output.space': 'Output space ℝᵐ',
  'ch15.consistent': 'consistent',
  'ch15.notconsistent': 'inconsistent',
  'ch15.kind.unique': 'Unique solution',
  'ch15.kind.line': 'Line of solutions',
  'ch15.kind.inconsistent': 'No solution · least-squares shown',
  'ch15.kind.all': 'All of ℝⁿ',
  'ch15.legend.rowspace': 'C(Aᵀ) row space',
  'ch15.legend.null': 'N(A) null space',
  'ch15.legend.solutions': 'x_p + t·n',
  'ch15.legend.colspace': 'C(A) column space',
  'ch15.legend.leftnull': 'N(Aᵀ) left null',
  'ch15.note':
    'After Strang × Hiranabe (CC-BY). Every Ax = b sits in this picture: x_p in the row space, plus any vector in the null space. b lands in the column space when consistent.',

  'ch16.eyebrow': 'Six algebraic shapes',
  'ch16.note':
    'After Hiranabe / Strang Practical Patterns (Figures 7–10, CC-BY). Same multiplication, six readings — column combinations, row combinations, diagonal scaling on either side, and rank-1 sums in the middle of three-matrix products.',
  'ch16.p1.title': 'A · c — column combination',
  'ch16.p1.note': 'A · c sums A’s columns weighted by c. The bedrock of Ax = b.',
  'ch16.p2.title': 'rᵀ · A — row combination',
  'ch16.p2.note': 'rᵀ · A sums A’s rows weighted by r. The dual of column combination.',
  'ch16.p1p.title': 'A · D — scale columns',
  'ch16.p1p.note': 'A · D scales each column of A by the matching diagonal entry of D.',
  'ch16.p2p.title': 'D · A — scale rows',
  'ch16.p2p.note': 'D · A scales each row of A by the matching diagonal entry of D.',
  'ch16.p3.title': 'X · D · c — vector recipe',
  'ch16.p3.note':
    'Appears in differential equations and recurrences: each component i of c is scaled by the i-th eigenvalue/diagonal then summed with the matching column of X.',
  'ch16.p4.title': 'Y · D · Xᵀ — rank-1 sum',
  'ch16.p4.note':
    'Y · D · Xᵀ = Σᵢ dᵢ · yᵢ · xᵢᵀ. The form behind both QΛQᵀ (symmetric EVD) and UΣVᵀ (SVD).',
};
